import React, { useState } from 'react';
import {
    Modal,
    FormLayout,
    TextField,
    Select,
    Text,
    BlockStack,
} from '@shopify/polaris';

import { validateNumeric } from '../../utils/inputSecurity';
import apiClient from '../../utils/api';

export const CommissionOverride = ({
    brand,
    availableMarketplaceTags,
    availableMarketplacesLoading,
    saving,
    showCommissionModal,
    setShowCommissionModal,
    setSaving,
    setError,
    fetchBrandData,
}) => {
    const [newOverrideTag, setNewOverrideTag] = useState('');
    const [newMktRate, setNewMktRate] = useState('');
    const [newTwRate, setNewTwRate] = useState('');

    const resetForm = () => {
        setNewOverrideTag('');
        setNewMktRate('');
        setNewTwRate('');
    };

    const addCommissionOverride = async () => {
        if (!brand) return;

        if (!newOverrideTag || newOverrideTag.trim() === '') {
            setError('Please select a marketplace');
            return;
        }

        const mktValue = parseFloat(newMktRate || '0');
        const twValue = parseFloat(newTwRate);

        if (isNaN(mktValue) || mktValue < 0 || mktValue > 100) {
            setError('Marketplace Commission Rate must be between 0 and 100');
            return;
        }
        if (isNaN(twValue) || twValue < 0 || twValue > 100) {
            setError('2W Commission Rate must be between 0 and 100');
            return;
        }

        try {
            setSaving(true);
            await apiClient.createCommissionOverride(brand.id, {
                orderTag: newOverrideTag,
                marketplaceCommissionRate: mktValue / 100,
                twCommissionRate: twValue / 100,
            });

            await fetchBrandData();
            setShowCommissionModal(false);
            resetForm();
        } catch (err) {
            setError(err.message || 'Failed to add commission override');
        } finally {
            setSaving(false);
        }
    };

    const unconfiguredMarketplaces = availableMarketplaceTags.filter(
        marketplace => !brand?.commissionOverrides?.some(
            override => override.orderTag === marketplace
        )
    );

    return (
        <Modal
            open={showCommissionModal}
            onClose={() => {
                setShowCommissionModal(false);
                resetForm();
            }}
            title="Add Commission Override"
            primaryAction={{
                content: 'Add Override',
                onAction: addCommissionOverride,
                loading: saving,
                disabled: !newOverrideTag || !newTwRate
            }}
            secondaryActions={[{
                content: 'Cancel',
                onAction: () => {
                    setShowCommissionModal(false);
                    resetForm();
                }
            }]}
        >
            <Modal.Section>
                <FormLayout>
                    <Select
                        label="Marketplace"
                        options={[
                            { label: 'Select marketplace...', value: '' },
                            ...unconfiguredMarketplaces.map(marketplace => ({
                                label: marketplace,
                                value: marketplace
                            }))
                        ]}
                        value={newOverrideTag}
                        onChange={setNewOverrideTag}
                        helpText={(() => {
                            if (availableMarketplacesLoading) return "Loading available marketplaces...";
                            if (unconfiguredMarketplaces.length > 0) return "Choose a marketplace for this commission override";
                            if (availableMarketplaceTags.length > 0) return "All available marketplaces already have overrides";
                            return "No marketplaces detected for this brand";
                        })()}
                        disabled={availableMarketplacesLoading || unconfiguredMarketplaces.length === 0}
                    />
                    <BlockStack gap="300">
                        <Text variant="headingSm" as="h4">Commission Rates</Text>
                        <TextField
                            label="Marketplace Commission Rate (%)"
                            type="number"
                            value={newMktRate}
                            onChange={setNewMktRate}
                            suffix="%"
                            min="0"
                            max="100"
                            step="0.1"
                            helpText="External marketplace fee (leave 0 if not applicable)"
                            autoComplete="off"
                        />
                        <TextField
                            label="2W Commission Rate (%)"
                            type="number"
                            value={newTwRate}
                            onChange={setNewTwRate}
                            suffix="%"
                            min="0"
                            max="100"
                            step="0.1"
                            helpText="2W service fee for this marketplace"
                            autoComplete="off"
                        />
                    </BlockStack>
                </FormLayout>
            </Modal.Section>
        </Modal>
    )
}
